import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "../../../components/ui/Calendar";
import { TimeSlotSection } from "../../../components/service/TimeSlotSection";
import { el } from 'date-fns/locale';
import useIsMobile from "../../../utils/useIsMobile";
import { format } from "date-fns";
import { Button } from "../../../components/ui/Button";
import { usePickUpAddress } from "../../../hooks/react-query/useNewService";
import { toast } from "../../../components/ui/Toast/useToast";
import { size } from "lodash";
import Loader from "../../../components/elements/loader";
import { TrifoldMapIcon } from "../../../icons";
import { buildGaragesCalendar, getGarageSelectionTitle, undoUntil } from "../serviceUtils";
import clsx from "clsx";
import { SERVICE_BOOKING_STEPS } from "../../../config/constants";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

export const Pickup = ({ state, dispatch, mode='form' }) => {

  const isMobile = useIsMobile(650)

  // LOCAL STATE: 
  const [addressSearchButtonDisabled, setAddressSearchButtonDisabled] = useState(true)
  const [isUndoLoading, setIsUndoLoading] = useState(false)
  const [futureDate, setFutureDate] = useState(new Date())
  const today = useMemo(() => new Date(), []);
  const [showMap, setShowMap] = useState(false)
  const [center, setCenter] = useState({
    lat: 37.9838,
    lng: 23.7275, // Athens
  });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [formattedAddress, setFormattedAddress] = useState("")
  const targetRef = useRef(null);
  
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDXFLTkJcZ-Pyn5uI0KLzUoyPZ8Ay1dRJk",
    libraries: ["places"],
    language: "el",
    region: "GR",
  });

  const containerStyle = {
    margin: '32px 0 0 0',
    width: '100%',
    height: '400px',
    borderRadius: '12px',
  };

  
  const mapRef = useRef(null);

  // FETCH DATA:

  // LOCAL VARIABLES:
  const {mutate: selectPickupAddress, isLoading: isPickupAddressLoading} = usePickUpAddress()
  
  // USE EFFECTS:

  useEffect(()=>{
    if(
      state?.pickupCRMFormAddress?.street && 
      state?.pickupCRMFormAddress?.number && 
      state?.pickupCRMFormAddress?.area && 
      state?.pickupCRMFormAddress?.zipCode
    ) {
      setAddressSearchButtonDisabled(false)
    }
    else {
      setAddressSearchButtonDisabled(true)
    }
  },[state?.pickupCRMFormAddress])

  useEffect(() => {
    if (!state?.calendarData?.length) return;

    const last = [...state.calendarData]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .at(-1);

    if (last?.startTime) {
      setFutureDate(new Date(last.startTime));
    }
  }, [state?.calendarData]);


  // HANDLERS & FUNCTIONS

  const getSlotsForDay = (slots, selectedDate) => {
    if (!selectedDate) return [];

    return slots?.filter(slot => {
      const slotDate = new Date(slot.startTime);
      return slotDate.toDateString() === selectedDate.toDateString();
    });
  };

  const availableDays = useMemo(() => {
    if (!Array.isArray(state?.calendarData)) return [];

    return state.calendarData
      .filter(s => s?.startTime)
      .map(s => {
        const d = new Date(s.startTime);
        return isNaN(d) ? null : d.toDateString();
      })
      .filter(Boolean);
  }, [state?.calendarData]);

  const daySlots = useMemo(() => {
    return getSlotsForDay(
      state?.calendarData,
      state.calendarValues.selectedDate
    );
  }, [state?.calendarData, state.calendarValues.selectedDate]);


  const searchPickupAddress = async () => {
    // const inputAddress = `${state?.pickupCRMFormAddress?.street} ${state?.pickupCRMFormAddress?.number}, ${state?.pickupCRMFormAddress?.area} ${state?.pickupCRMFormAddress?.zipCode} Ελλάδα`
    // const payload = { pickupDeliveryAddress: inputAddress}
    const payload = { pickupDeliveryAddress: formattedAddress}
    try{
        selectPickupAddress({serviceFlowId:state?.serviceFlowId, payload},
        {
          onSuccess : (response) => {
            if (response && response.exceptions) {
              if (size(response.exceptions) > 0) {
                const message = response?.exceptions?.pickupDeliveryAddress?.[0]
                toast({
                  variant: 'destructive',
                  description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
                });
              }
            } else {
              const bookingSelectOptions =
                response?.inputs?.bookingSelect?.options || response?.inputs?.preferredGarage?.options;
    
              const bookingCalendarOptions =
                response?.inputs?.bookingCalendar?.options?.map((option) => {return {startTime: option?.StartTime, endTime:option?.EndTime}}) || 
                response?.inputs?.preferredTimeslots?.options?.map((option) => {return {startTime: option?.StartTime, endTime:option?.EndTime}});
    
              const normalized = buildGaragesCalendar(
                bookingSelectOptions,
                bookingCalendarOptions
              );
              const fdate = new Date(bookingCalendarOptions[bookingCalendarOptions?.length - 1]?.startTime)
              setFutureDate(fdate);
              dispatch({ type: "SET_STEPSTATUS", stepStatus: response?.name });
              dispatch({ type: 'CLEAR_TIMESLOTS' })
              dispatch({ type: "SET_GARAGE_CALENDAR_DATA", garageCalendarData: normalized})
              dispatch({ type: "SET_CALENDAR_DATA", calendarData: bookingCalendarOptions})
              dispatch({ type: "SET_SELECTED_GARAGE", selectedGarage: normalized?.[0]})
              dispatch({
                  type: 'SET_SELECTED_DATE',
                  value: null,
                })
              dispatch({
                type: 'CLEAR_TIMESLOTS',
              })
              setShowMap(false)
              setMarkerPosition(null)
              if (targetRef.current) {
                targetRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "end"
                });
              }
            }
          }
        }
      )} catch(e) {
        console.warn(e)
        toast({
          variant: 'destructive',
          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
        });
      }
  }

  const geocodeAddress = (address) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        
        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };

        setFormattedAddress(results?.[0]?.formatted_address)
        setCenter(coords);
        setMarkerPosition(coords);
        // mapRef.current.panTo(coords);
        // mapRef.current.setZoom(15); 
      } else {
        console.error("Geocode failed:", status);
      }
    });
  };

  return (
    <div className='flex flex-col gap-6 pb-8'>
    {(isPickupAddressLoading || isUndoLoading) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-[2px]">
          <Loader />
        </div>
      )}
      <h3>1. Διεύθυνση παραλαβής</h3>
      <div className={`"shadow-md rounded-xl py-6 px-9 bg-white`}>
        <h4 className='pb-6'>Συμπλήρωσε τη διεύθυνση για τον προγραμματισμό του Pick-Up & Delivery.</h4>
        <div className='grid grid-cols-9 gap-5'>
          <div className='grid col-span-6'>
            <div className='flex flex-col gap-3'>
              <div>
                <p className='text-sm text-grenite pb-2'>Οδός</p>
                <input
                  disabled={false}
                  className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                  name={`street-pickup`}
                  value={state?.pickupCRMFormAddress?.street}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_PICKUP_ADDRESS',
                      option: "street",
                      value: e?.target?.value,
                    })
                  }
                  />
              </div>
              <div>
                <p className='text-sm text-grenite pb-2'>Περιοχή</p>
                <input
                  disabled={false}
                  className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                  name={`street-area`}
                  value={state?.pickupCRMFormAddress?.area}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_PICKUP_ADDRESS',
                      option: "area",
                      value: e?.target?.value,
                    })
                  }
                  />
              </div>
            </div>
          </div>
          <div className='grid col-span-3'>
            <div className='flex flex-col gap-3'>
              <div>
                <p className='text-sm text-grenite pb-2'>Αριθμός</p>
                <input
                  disabled={false}
                  className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                  name={`street-number`}
                  value={state?.pickupCRMFormAddress?.number}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_PICKUP_ADDRESS',
                      option: "number",
                      value: e?.target?.value,
                    })
                  }
                  />
                </div>
                <div>
                <p className='text-sm text-grenite pb-2'>Ταχ. κώδικας</p>
                <input
                  disabled={false}
                  className="w-full text-black p-4 border border-fadedgrey rounded-lg"
                  name={`street-zipCode`}
                  value={state?.pickupCRMFormAddress?.zipCode}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_PICKUP_ADDRESS',
                      option: "zipCode",
                      value: e?.target?.value,
                    })
                  }
                  />
                </div>
            </div>
          </div>
        </div>
        <div className='flex flex-row items-end justify-end pt-3'>
          <Button className={`${addressSearchButtonDisabled ? "text-fadedgrey" :"text-primary"} font-semibold` }
            disabled={addressSearchButtonDisabled}
            onClick={async ()=>{
              const inputAddress = `${state?.pickupCRMFormAddress?.street} ${state?.pickupCRMFormAddress?.number}, ${state?.pickupCRMFormAddress?.area}, ${state?.pickupCRMFormAddress?.zipCode}`
              geocodeAddress(inputAddress)
              setShowMap(true)
              }}>
            Εύρεση διεύθυνσης
          </Button>
        </div>
        {isLoaded ? showMap && <div>
          <h5 className='pt-5 pb-3'>Επιβεβαίωσε ότι η τοποθεσία στον χάρτη είναι σωστή. Αν όχι, τροποποίησε τα στοιχεία που συμπλήρωσες και αναζήτησε ξανά τη διεύθυνση.</h5>
          <h5 className='font-bold'>{formattedAddress}</h5>
          <GoogleMap
            key={showMap ? "map-open" : "map-closed"}
            mapContainerStyle={containerStyle}
            center={center}
            zoom={15}
            onLoad={(map) => (mapRef.current = map)}
            onUnmount={() => {
              mapRef.current = null
            }}
            options={{
              zoomControl: false, // Disable zoom control
              fullscreenControl: false, // Disable fullscreen control
              streetViewControl: false, // Disable street view control
              mapTypeControl: false, // Disable map type control
            }}
          >{markerPosition && <Marker position={markerPosition} />}</GoogleMap>
          <div className='flex flex-row items-end justify-end pt-3'>
            <Button className={`${addressSearchButtonDisabled ? "text-fadedgrey" :"text-primary"} font-semibold` }
              disabled={addressSearchButtonDisabled}
              onClick={async ()=>{
                if(SERVICE_BOOKING_STEPS[state?.stepStatus] > SERVICE_BOOKING_STEPS["pickupDeliveryAddress"]){
                  setIsUndoLoading(true)
                  try{
                    const undoStatus = await undoUntil({targetStep : "pickupDeliveryAddress", state, dispatch})
                    if(undoStatus?.success) {
                      searchPickupAddress()
                    } else {
                      toast({
                        variant: 'destructive',
                        description: undoStatus?.message,
                      });
                    }
                  } catch (e) {
                    console.warn(e)
                  } finally {setIsUndoLoading(false)}
                }else {
                  searchPickupAddress()
                }
                }}>
              Επιβεβαίωση
            </Button>
          </div>
        </div> : <Loader />}
      </div>
      {!showMap && state?.selectedGarage && mode==='form' && <>
        <h3>{`2. ${getGarageSelectionTitle({state})}`}</h3>
        <div className={`shadow-md rounded-xl py-6 pl-9 pr-6 ${state?.selectedGarage?.garageName ? "bg-white":"bg-powdergrey cursor-not-allowed"}`}>
          {state?.selectedGarage ? <>
            <h4 className='pb-3'>{state?.selectedGarage?.garageName || "Όνομα Συνεργείου"}</h4>
            <div className={`flex ${isMobile ? "flex-col gap-4" :"flex-row justify-between items-end"} `}>
              <div className='flex flex-col gap-2'>
                <p className='text-sm'>{state?.selectedGarage?.address || "Διεύθυνση"}</p>
                <div className='flex flex-row gap-3'>
                  <button 
                    disabled={!state?.selectedGarage?.garageName} 
                    className='bg-none flex flex-row gap-2 items-center' 
                    onClick={()=>{
                      const googleMapsUrl = `https://www.google.com/maps/place/${state?.selectedGarage?.coordinates?.lat},${state?.selectedGarage?.coordinates?.lng}`
                      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");           
                    }}>
                      <TrifoldMapIcon className={`w-4 h-4 ${state?.selectedGarage?.garageName ? "text-primary " : "text-fadedgrey"}`} />
                      <p className={`text-sm ${state?.selectedGarage?.garageName ? "text-primary " : "text-fadedgrey"} font-semibold`}>Δες το στον χάρτη</p>
                  </button>
                  {state?.selectedGarage?.distanceFromUser && <p className="text-sm text-grenite">{(state?.selectedGarage?.distanceFromUser/1000).toFixed(1)} km</p>}
                </div>
              </div>
              {state?.garageCalendarData?.length > 1 && <button 
                disabled={!state?.selectedGarage?.garageName} 
                className={`rounded-md py-3 px-11 text-sm font-semibold ${state?.selectedGarage?.garageName ? 'border border-fadedgrey' : "bg-fadedgrey text-white"}`} 
                onClick={()=>{
                  // render different component from parent
                  if(state?.stepThree === 'FirstAvailableAvisCalendar') {
                    dispatch({ type: "SET_STEP_THREE", stepThree: "FirstAvailableAvis"});
                  } else {
                    if(state?.stepThree === 'PickupAvis') {
                      dispatch({ type: "SET_STEP_THREE", stepThree: "PickupAvisGarages"});
                    }
                    if(state?.stepThree === 'PickupCRMForm') {
                      dispatch({ type: "SET_STEP_THREE", stepThree: "CRMFormGarages"});
                    }
                  }
                }}>
                  Αλλαγή
              </button>}
            </div>
          </> : <h4 className='pb-3'>Δεν υπάρχει διαθέσιμο συνεργείο</h4>}
        </div>
      </>}
      {!showMap && availableDays?.length > 0 && <>
        <h3>{`${mode==='appointment' ? "2" : "3"}. Επιλογή ημερομηνίας & ώρας`}</h3>
        <div className="bg-white shadow-md rounded-xl py-6 px-9 flex flex-col gap-10">
          <h4>{['PickupCRMForm','CRMForm'].includes(state?.stepThree) ? "Μπορείς να επιλέξεις έως και 3 εναλλακτικές." : "Ποιο ραντεβού σε εξυπηρετεί;"}</h4>
          <div className="grid grid-cols-7 flex-1 min-h-0">
            <div className="col-span-5 h-full">
              <Calendar
                locale={el}
                fromDate={today}
                toDate={futureDate}
                mode="single"
                selected={state.calendarValues.selectedDate}
                onSelect={(date) =>
                  dispatch({
                    type: 'SET_SELECTED_DATE',
                    value: date,
                  })
                }
                disabled={(date) =>{
                  const key = date.toDateString();
                  return !availableDays.includes(key);
                }}
                modifiers={{
                  hasSlot: state.calendarValues.selectedSlots.map(
                    slot => new Date(slot.date + "T00:00:00")
                  )
                }}
                modifiersClassNames={{
                  hasSlot: "bg-[#D4002A]/15 rounded-full"
                }}
                className="lg:p-3 py-6 bg-transparent lg:shadow-none rounded-none border border-b-0 border-l-0 border-t-0 border-r-fadedgrey"
                classNames={{
                  row: 'flex w-full justify-between py-[11px]',
                  head_row: 'flex w-full justify-between py-[11px] mb-7',
                  nav_button_previous: 'absolute left-10',
                  nav_button_next: 'absolute right-10',
                  day: clsx(
                      'p-0 font-normal aria-selected:opacity-100 w-8 rounded-full h-8'
                    ),
                }}
              />
            </div>
            <div className="col-span-2 flex flex-col gap-6 pt-4 h-full min-h-0">
              <h4 className="pl-7 shrink-0">Ώρα</h4>
              <TimeSlotSection
                currentSlots={daySlots}
                selectedSlots={state.calendarValues.selectedSlots}
                dispatch={dispatch}
                state={state}
                maxSelections={mode==="appointment" ? 1 : 3}
              />
            </div>
          </div>
          {mode==="form" && state?.calendarValues?.selectedSlots?.length > 0 && <div className='w-full flex flex-col gap-2'>
            {state?.calendarValues?.selectedSlots?.length > 0 && <button className='text-sm text-[#D4002A] flex flex-row w-full justify-end pr-10' 
            onClick={()=>{
              dispatch({
                type: 'CLEAR_TIMESLOTS',
              })
            }}>Διαγραφή όλων</button>}
          <div className='grid grid-cols-2 gap-2 w-full'>
            {state?.calendarValues?.selectedSlots?.map((slot,index) => {
              const d = new Date(slot.date + "T00:00:00");
              const formatted = `${format(d, "EEEE dd.MM", { locale: el })} ${slot?.start} - ${slot?.end}`;
              return (
                <div key={index} className='flex flex-row justify-between items-center border border-fadedgrey rounded-full py-3 pr-3 pl-5'>
                    <p className='text-lg font-bold '>{formatted}</p>
                    <button onClick={()=>{
                      dispatch({
                        type: 'REMOVE_TIMESLOT',
                        value: slot,
                      })
                    }}
                    className="px-[5px] text-grenite bg-powdergrey rounded-full">
                      🞩
                    </button>
                  </div>
                )
              })}
            </div>
          </div> }
        </div>
      </> } 
    </div>
  );
};
